import { Alert, DialogContent, DialogProps, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { FlightInfo, getDestinationContactFromOrderInfo, getTotalWeight, OrderInfo } from 'adoms-common-lib';
import React from 'react';
import { makeStyles } from 'tss-react/mui';


const useStyles = makeStyles()((theme: any) => ({
    root: {
        display: 'flex',
        backgroundColor: theme.palette.background.default,
    },
    drawerHeader: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0, 1),
        // necessary for content to be below app bar
        ...theme.mixins.toolbar,
        justifyContent: 'flex-end',
    },
    contentShift: {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
    },


    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },

    selectEmpty: {
        marginTop: theme.spacing(2),
    },

    deliveryTable: {
        minWidth: 1024,
    },

    flightInfoInputTable: {
        minWidth: 512,
    },

    newButton: {
        position: 'fixed',
        top: theme.spacing(9),
        right: theme.spacing(3),
    },

    textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: 200,
    },


    select: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
    },

    stepper: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,

    },
    table: {
        minWidth: 100,
    },
}));

type FlightOrderCombinationDialogContentProps = {
    /**
     * 配送依頼を紐付けるフライト
     */
    flight: FlightInfo,
    /**
     * ダイアログがオープンしているかどうか
     */
    isOpen: boolean,
    /**
     * エラーフラグ
     */
    isErrorFlag: boolean,
    /**
     * 選択可能なオーダーの一覧
     */
    orderList: OrderInfo[],
    /**
     * 配送依頼が選択されたときの処理
     */
    handleChangeOrder: (event: any) => void,
    /**
     * 選択されたOrderIDを記録する関数
     */
    setSelectedOrder: React.Dispatch<React.SetStateAction<string>>,
    /**
     * 選択されたOrderID
     */
    selectedOrder: string,
    /**
     * 追加された配送依頼のリスト
     */
    addedOrder: OrderInfo[]
}

//FlightOrderCombinationDialogの内容を表示するコンポーネント
const FlightOrderCombinationDialogContent: React.FC<FlightOrderCombinationDialogContentProps> = (props) => {
    const { classes } = useStyles();
    const [scroll, setScroll] = React.useState<DialogProps['scroll']>('body');
    const order = props.orderList.find((v) => v.orderID === props.selectedOrder);

    const destinationContact = React.useMemo(() => {
        return order ? getDestinationContactFromOrderInfo(order) : undefined;
    }, [order]);

    const totalWeight = React.useMemo(() => {
        if (!order) {
            return 0;
        }

        const addedOrdersWeight = props.addedOrder.reduce((sum, orderInfo) => {
            return sum + getTotalWeight(orderInfo.goods);
        }, 0);

        return addedOrdersWeight + getTotalWeight(order.goods);
    }, [order, props.addedOrder]);

    if (props.isErrorFlag) {
        return (
            <DialogContent dividers={scroll === 'paper'}>
                <Alert severity="error">配送依頼の処理中にエラーが発生しました。再試行してもエラーが発生する場合、システム管理者へご連絡ください</Alert>
            </DialogContent>
        );
    }

    return (
        <DialogContent dividers={scroll === 'paper'}>
            {props.orderList.length === 0 ? (
                <Alert severity="info">このフライトで配送可能な依頼がありませんでした。</Alert>
            ) : (
                <div>
                    <InputLabel className={classes.select} id="demo-simple-select-outlined-label">配送依頼</InputLabel>
                    <Select
                        variant="standard"
                        className={classes.textField}
                        labelId="drone"
                        onChange={props.handleChangeOrder}
                        value={props.selectedOrder}
                        id="order"
                        label="配送依頼">
                        {props.orderList.map((order: OrderInfo, i: number) => (
                            <MenuItem key={order.orderID} value={order.orderID}>{order.preferredDeliveryDate + " " + order.preferredDeliveryTime + "" + "（" + order.orderID + "）"}</MenuItem>
                        ))}
                    </Select>
                </div>
            )}

            {props.orderList.some((v) => v.orderID === props.selectedOrder) ? (
                <TableContainer component={Paper} elevation={0}>
                    <Table className={classes.table} aria-label="simple table">
                        <TableBody>
                            <TableRow >
                                <TableCell>ビジネスパートナーID</TableCell>
                                <TableCell>{order!!.businessPartnerId}</TableCell>
                            </TableRow>
                            <TableRow >
                                <TableCell>確認番号</TableCell>
                                <TableCell>{order!!.orderID}</TableCell>
                            </TableRow>
                            <TableRow >
                                <TableCell>外部連携ID</TableCell>
                                <TableCell>{order!!.externalOrderID !== undefined ? order!!.externalOrderID : "設定なし"}</TableCell>
                            </TableRow>
                            <TableRow >
                                <TableCell>配送希望時間</TableCell>
                                <TableCell> {order!!.preferredDeliveryDate + " " + order!!.preferredDeliveryTime} </TableCell>
                            </TableRow>
                            <TableRow >
                                <TableCell>受け取り人</TableCell>
                                <TableCell>{destinationContact?.lastname + " " + destinationContact?.firstname}様</TableCell>
                            </TableRow>
                            <TableRow >
                                <TableCell>ペイロード</TableCell>
                                <TableCell>{getTotalWeight(order!!.goods)}g</TableCell>
                            </TableRow>
                            <TableRow >
                                <TableCell>合計重量</TableCell>
                                <TableCell sx={totalWeight > 7500 ? { color: 'error.main' } : {}}>
                                    {String(totalWeight)}g
                                    {totalWeight > 7500 && ' (重量上限超過)'}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : null}
        </DialogContent>
    );
}

export default FlightOrderCombinationDialogContent;